import { Link } from 'react-router';
import { useBranding } from '~/lib/branding';
import {
  NewLeafFooterBlurb,
  NewLeafFooterColumns,
  NewLeafFooterHours,
  NewLeafFooterLocation,
} from '~/components/layout/new-leaf-footer-content';

/*
 * NO `SITE` IMPORT, DELIBERATELY — and the unit test in
 * tests/unit/tenant-brand-leak.test.ts fails if one comes back.
 *
 * This footer renders on every public page of every tenant. Everything it said
 * about the company was New Leaf's: `/images/nlc-logo.png` as the mark,
 * `SITE.tagline`, a literal "Residential cleaning", `SITE.serviceArea`,
 * `SITE.founded`, New Leaf's opening hours, a residential service menu, and a
 * W-2 employment claim. Under the tenant's own NAME, which is what made it read
 * as one company rather than as a bug.
 *
 * What remains here is the SKELETON — the parts that are true for any company
 * on the platform. New Leaf's own content lives in `new-leaf-footer-content`
 * and renders behind one `isNewLeaf` gate.
 */
export function PublicFooter() {
  const branding = useBranding();
  return (
    <footer className="site-footer">
      <div className="container">
        {/* Brand block */}
        <div className="foot-col foot-brand">
          <div className="foot-brand-logo">
            {/*
              A company with no logo of its own shows its NAME. Falling back to
              another company's mark is the bug, not the safety net — the alt
              text carried the tenant's name while the image carried New Leaf's.
            */}
            {branding.logoUrl && (
              <img src={branding.logoUrl} alt="" className="footer-logo-img" aria-hidden="true" />
            )}
            <span className="footer-brand-name">{branding.name}</span>
          </div>
          {branding.isNewLeaf && <NewLeafFooterBlurb />}
        </div>

        {branding.isNewLeaf && <NewLeafFooterColumns />}

        {/* Contact — laid out inline so phone/email/hours occupy one row on
            mobile instead of three stacked lines. */}
        <div className="foot-col foot-col-contact">
          <h2 className="footer-col-heading">Contact</h2>
          {/*
            Contact details render only when THIS company has them. The footer
            is on every public page, so a hard-coded address here put New Leaf's
            phone and email in front of every other tenant's visitors.
          */}
          <div className="foot-contact-row">
            {branding.phone && branding.phoneHref && (
              <a href={branding.phoneHref}>{branding.phone}</a>
            )}
            {branding.email && (
              <a href={`mailto:${branding.email}`}>{branding.email}</a>
            )}
            {branding.isNewLeaf && <NewLeafFooterHours />}
          </div>
          <Link to="/quote" className="btn btn-brass foot-cta">
            Get a quote →
          </Link>
          {/*
            Hiring. Kept in step with the same button on the platform's footer
            (nlc-platform PR #38) so this site can take over the domain without
            the hiring door quietly disappearing.

            Outlined rather than brass: the quote is the business, the hire is
            the second door, and two filled buttons make a visitor choose before
            they have read either.
          */}
          <Link to="/apply" className="btn foot-cta foot-cta-hire">
            Join the team →
          </Link>
        </div>
      </div>

      <div className="foot-bottom">
        <span>
          © {new Date().getFullYear()} {branding.name}
          {branding.isNewLeaf && <NewLeafFooterLocation />}
        </span>
        {/* An employment-model claim about one company. Asserting it on behalf
            of another is a statement we have no basis for. */}
        {branding.isNewLeaf && <span>W-2 employees. Every one.</span>}
      </div>
    </footer>
  );
}
